import DoctorCoverageWorkspace from './DoctorCoverageWorkspace';
import { doctorCoverageApi } from './doctor-coverage-api';
import { useMemo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { CocClinicSummary } from '@/dashboard/types';
import { fetchCocCallActionSummary, fetchCocClinics } from '@/dashboard/api';
import DashboardLoadError from '@/dashboard/DashboardLoadError';
import { portalErrorStatus, safeErrorMessage } from '@/lib/portal-api';
import { CallActionWorkbenchPanel } from '@/dashboard/CallActionWorkbenchPanel';
import { FIXTURE_BACKED_PANELS_ENABLED } from '@/dashboard/fixture-panels';
import CocStationSection from '@/dashboard/coc/CocStationSection';
import GlobalPatientSearchModal from '@/dashboard/patients/GlobalPatientSearchModal';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { AcButton, PageHeader } from '@/dashboard/ui';

/**
 * "1 clinics - 1 need attention" was wrong twice in one line: a plural noun
 * on a count of one, and a plural verb after it. Both agree with their own
 * number now.
 */
export function cocClinicsSummaryLine(
  clinicCount: number,
  alertCount: number,
): string {
  const clinics = `${clinicCount} ${clinicCount === 1 ? 'clinic' : 'clinics'}`;
  const attention = `${alertCount} ${alertCount === 1 ? 'needs' : 'need'} attention`;
  return `${clinics} \u00b7 ${attention}`;
}

export interface CocDashboardPageProps { view?: 'home' | 'coverage' }

export default function CocDashboardPage({ view = 'home' }: CocDashboardPageProps = {}) {
  const ctx = useDashboardMeContext();
  // PATIENT-SEARCH-V1: the search door rides the EXISTING server-computed
  // signal -- present only when the patient-master flag is on AND the role
  // is in the create audience (receptionist / coc / gm_agm / owner). Absence
  // is disabled (fail-closed); no new flag, no widened fence.
  const patientSearchEnabled = ctx?.me?.patientMasterEnabled === true;
  const [coverageDate, setCoverageDate] = useState(() => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()));
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [heroQuery, setHeroQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [clinics, setClinics] = useState<CocClinicSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (view === 'coverage' && ctx?.me?.role !== 'coc') {
      setClinics([]); setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setErrorStatus(undefined);
    void fetchCocClinics()
      .then((rows) => {
        if (!cancelled) setClinics(rows);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(safeErrorMessage(e, 'Something went wrong. Retry.'));
          setErrorStatus(portalErrorStatus(e));
          setClinics([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [view, ctx?.me?.role]);

  const alertClinics = useMemo(
    () =>
      clinics.filter(
        (c) =>
          !c.doctorConfirmed ||
          !c.billingSubmitted ||
          c.openNoShows > 0 ||
          c.alertCount > 0,
      ),
    [clinics],
  );
  const okClinics = useMemo(
    () =>
      clinics.filter(
        (c) =>
          c.doctorConfirmed &&
          c.billingSubmitted &&
          c.openNoShows === 0 &&
          c.alertCount === 0,
      ),
    [clinics],
  );

  const doctorConfirmedCount = clinics.filter((c) => c.doctorConfirmed).length;
  const billingSubmittedCount = clinics.filter((c) => c.billingSubmitted).length;
  const billingPendingCount = clinics.filter((c) => !c.billingSubmitted).length;
  const totalNoShows = clinics.reduce((s, c) => s + c.openNoShows, 0);

  // Factory owns the route and nav entry. This mode is the lane-owned page
  // composition; it grants no API access and never removes the default home.
  if (view === 'coverage') {
    if (ctx?.loading) return <div className="coc-dashboard"><p role="status">Loading doctor coverage...</p></div>;
    if (ctx?.me?.role !== 'coc') return <div className="coc-dashboard"><p role="alert">Doctor coverage is unavailable for this role.</p></div>;
    return <div className="coc-dashboard">
      {loading ? <><PageHeader title="Doctor coverage"/><p role="status">Loading clinics...</p></> : error ?
        <><PageHeader title="Doctor coverage"/><DashboardLoadError message={error} {...(errorStatus !== undefined ? {status:errorStatus} : {})}/></> :
        <DoctorCoverageWorkspace clinics={clinics.map(c=>({clinicId:Number(c.clinicId),clinicName:c.clinicName}))}
          date={coverageDate} onDateChange={setCoverageDate} allowed authenticatedCoc service={doctorCoverageApi}/>}
    </div>;
  }

  return (
    <div className="coc-dashboard">
      {/* COC-STATION-V1 (V4-P1): the station is the default COC work queue -- the daily
          work queue with in-place actions (the 360 law). The clinic-status
          content below stays (ops monitoring is COC duty, tenant COC-CSD
          s.5). An explicitly opened coverage workspace precedes the station;
          closing its existing door returns to the same home controls. */}
      {ctx?.me?.role === 'coc' ? <>
        {!coverageOpen ? <AcButton variant="secondary" onClick={() => setCoverageOpen(true)}>Doctor coverage</AcButton> : null}
        {coverageOpen ? <DoctorCoverageWorkspace clinics={clinics.map(c => ({ clinicId: Number(c.clinicId), clinicName: c.clinicName }))}
          date={coverageDate} onDateChange={setCoverageDate}
          allowed={true} authenticatedCoc={ctx.me.role === 'coc'} service={doctorCoverageApi} /> : null}
        {coverageOpen ? <AcButton variant="secondary" onClick={() => setCoverageOpen(false)}>Doctor coverage</AcButton> : null}
      </> : null}

      <CocStationSection />

      {/* UNIFIED-SHELL-AND-ROLE-NAVIGATION-V1 (V7-P4 Slice 2): the shared
          role-home header. The attention-now framing (how many clinics need
          attention) rides the context sub -- an existing derivation, no new
          business logic. The Needs-attention / Running-normally sections below
          remain the attention region. */}
      <PageHeader
        eyebrow="COC"
        title="My clinics"
        subs={
          !error
            ? [
                cocClinicsSummaryLine(clinics.length, alertClinics.length),
              ]
            : []
        }
      />

      {/* PATIENT-SEARCH-V1 (Partha COC rows "Search by: Mobile" / "Patient
          name" / "UHID / UMR" / "Appointment ID" / the search-first check):
          THE SAME door the front desk and the doctor already carry -- the
          FO-3 hero classes, the same submit rule, and THE ONE
          GlobalPatientSearchModal as the room. One box recognises what was
          typed: the wire picks phone / UMR / name itself and resolves an
          appointment id beside the person search.

          NO NAV DOOR IS ADDED. The COC nav block keeps the founder's
          do-NOT-expose list (the generic Patients browse) intact; reach is
          restored the way the doctor's was when the same tab was removed --
          a home-mounted search, "no capability is lost, only the tab". The
          modal's desk context is used unchanged: every door it wires
          (record, register, book, enquiry continue, appointment day) is a
          capability the server already grants this role, so none is dead
          and no fence moves. */}
      {patientSearchEnabled ? (
        <section className="today-home__hero" aria-label="Find the patient">
          <form
            className="today-home__search"
            onSubmit={(e) => {
              e.preventDefault();
              if (heroQuery.trim().length >= 2) setSearchOpen(true);
            }}
          >
            <input
              className="today-home__search-input"
              type="search"
              value={heroQuery}
              placeholder="Mobile / UMR / name / appointment no."
              aria-label="Search patients by mobile, UMR, name, or appointment number"
              autoComplete="off"
              onChange={(e) => setHeroQuery(e.target.value)}
              data-testid="coc-home-global-search-input"
            />
            {/* THE ONE AcButton owner (design-system RULE 3) -- this screen
                is not on the raw-button grandfather list and never will be. */}
            <AcButton
              type="submit"
              variant="primary"
              disabled={heroQuery.trim().length < 2}
              data-testid="coc-home-global-search-submit"
            >
              Search
            </AcButton>
          </form>
          {/* NO HINT LINE. CROSS-ROLE-HOME-GRAMMAR-V1 (founder ruling D5)
              already deleted the mode-listing sentence from the front desk
              and the doctor -- "helper copy survives only where it adds
              something the control cannot say". The COC home was the one
              role home that ruling never reached, so it still carried
              "Search by mobile number, UMR (e.g. AC-000123), patient name,
              or appointment number" under a placeholder that reads "Mobile /
              UMR / name / appointment no.". The rule is applied here, not
              re-invented: the sentence is GONE, the placeholder is the
              contract, and no search MODE changed. The D5 guard now walks
              this home too, so it cannot come back. */}
        </section>
      ) : null}

      {error ? (
        <div className="mb-4">
          <DashboardLoadError
            message={error}
            {...(errorStatus !== undefined ? { status: errorStatus } : {})}
          />
        </div>
      ) : null}

      {!error ? (
        // Reuses the SAME canonical tile carrier CocStationSection renders
        // directly above (.coc-station__tile*), never a page-local family --
        // COC-CALLCENTRE-VISUAL-CONFORMANCE-V1 vocabulary conformance.
        <div className="coc-station__tiles">
          <div className="coc-station__tile">
            <span className="coc-station__tile-value">
              {doctorConfirmedCount}/{clinics.length}
            </span>
            <span className="coc-station__tile-label">Doctors confirmed</span>
          </div>
          <div className={`coc-station__tile ${billingPendingCount > 0 ? 'coc-station__tile--warn' : ''}`}>
            <span className="coc-station__tile-value">
              {billingSubmittedCount}/{clinics.length}
            </span>
            <span className="coc-station__tile-label">Billing submitted</span>
          </div>
          <div className={`coc-station__tile ${totalNoShows > 0 ? 'coc-station__tile--warn' : ''}`}>
            <span className="coc-station__tile-value">{totalNoShows}</span>
            <span className="coc-station__tile-label">Open no-shows</span>
          </div>
          {/* NO "Total alerts" TILE. The masthead directly above already
              reads "N clinics - M needs attention", which is this same
              derivation (alertClinics) in the sentence the reader arrives
              on. A figure restated one line later is not emphasis. */}
        </div>
      ) : null}

      {alertClinics.length > 0 ? (
        <section>
          <h2 className="clinic-section__title">Needs attention</h2>
          {alertClinics.map((c) => (
            <Link
              key={c.clinicId}
              className="clinic-row clinic-row--alert"
              to={`/dashboard/coc/clinic/${c.clinicId}`}
            >
              <div className="clinic-row__name">{c.clinicName}</div>
              <div className="flex flex-wrap gap-1">
                {!c.doctorConfirmed ? (
                  <span className="ac-badge ac-badge--danger">No doctor</span>
                ) : null}
                {!c.billingSubmitted ? (
                  <span className="ac-badge ac-badge--pending">Billing pending</span>
                ) : null}
                {c.openNoShows > 0 ? (
                  <span className="ac-badge ac-badge--pending">
                    {c.openNoShows} no-show{c.openNoShows > 1 ? 's' : ''}
                  </span>
                ) : null}
              </div>
              <span>›</span>
            </Link>
          ))}
        </section>
      ) : null}

      {okClinics.length > 0 ? (
        <section>
          <h2 className="clinic-section__title">Running normally</h2>
          {okClinics.map((c) => (
            <Link
              key={c.clinicId}
              className="clinic-row clinic-row--ok"
              to={`/dashboard/coc/clinic/${c.clinicId}`}
            >
              <div className="clinic-row__name">{c.clinicName}</div>
              <div className="clinic-row__stats text-sm text-muted-foreground">
                <span>{c.patientsToday} patients</span>
                {c.revenueToday != null ? (
                  <span className="ml-2">₹{c.revenueToday.toLocaleString('en-IN')}</span>
                ) : null}
              </div>
              <span>›</span>
            </Link>
          ))}
        </section>
      ) : null}

      {loading ? <div className="loading">Loading your clinics…</div> : null}

      {!loading && !error && clinics.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">No clinics assigned to your scope.</p>
      ) : null}

      {/*
        CRM-ACTION-WORKBENCH-PANEL-01: COC clinic-scoped Call Action
        Workbench. Self-fetching + fault-isolated; consumes the existing
        /api/coc/call-action-summary endpoint (no backend change). Its only
        data path today is sample/fixture-backed, so it is kept out of the
        normal journey by the shared FIXTURE_BACKED_PANELS_ENABLED guardrail
        (default off) and a product-safe placeholder renders in its place.
      */}
      {!error && FIXTURE_BACKED_PANELS_ENABLED ? (
        <CallActionWorkbenchPanel
          fetcher={fetchCocCallActionSummary}
          title="Clinic Call Action Workbench"
        />
      ) : !error ? (
        <section
          className="clinic-table"
          aria-label="Clinic call activity"
          data-testid="coc-call-activity-placeholder"
        >
          <h2 className="clinic-table__title">Call activity</h2>
          <p className="clinic-table__subtitle">
            Call activity will appear here when available.
          </p>
        </section>
      ) : null}

      {/* RESTORED (founder corrective, PR #683 held). An earlier revision of
          this package DELETED these three doors to win three elements,
          reasoning that the primary nav carries the same destinations. That
          was the wrong deletion and the rule is now explicit: the element
          count means VISIBLE ELEMENTS, NEVER CAPABILITIES -- hide or
          consolidate, never delete a working control. These are the only
          three interactive elements the package removed, and they are back
          unchanged. The count was found again where it is genuinely
          redundant: a zero tile that cost a whole card to say nothing. */}
      <nav className="bottom-nav">
        <Link to="/dashboard/coc/no-shows" className="bottom-nav__item">
          No-shows
        </Link>
        <Link to="/dashboard/coc/patient-followups" className="bottom-nav__item">
          Patient follow-ups
        </Link>
        <Link to="/dashboard/coc/billing" className="bottom-nav__item">
          Billing
        </Link>
      </nav>

      {/* PATIENT-SEARCH-V1: THE ONE search room (REUSE-THE-DOOR) -- the same
          centered modal the front desk and the doctor open. It already
          carries the four searches, the shared-phone family list, the
          existing-patient check, and the single "Register patient" action
          that opens THE ONE register field set; nothing here forks it.

          walkinClinicId is null because the COC works ACROSS clinics and
          this home has no single active clinic -- the documented null
          semantic skips the walk-in arrival record and never blocks the
          person create. */}
      {searchOpen ? (
        <GlobalPatientSearchModal
          initialQuery={heroQuery}
          onClose={() => setSearchOpen(false)}
          walkinClinicId={null}
        />
      ) : null}
    </div>
  );
}
